import {getTodayReservationList, signReOrder, getTodayReservationStat, getUnifiedClinicQueue, registerOnsiteFree, registerOnsitePaid, cancelOnsitePaid, clinicSqbReservationPay, clinicSqbReservationQueryResult, getOnsiteSchedules, searchReservationPatients, matchReservationPatient, registerReturnVisit, completeReturnVisit, cancelReturnVisit} from "@/api";
import {ElMessageBox} from "element-plus";
import {message, errorMsg, getTodayDate} from "@/assets/utils/common";
import baseConfig from "@/api/config";

/**
 * 付费挂号 pending 自动轮询窗口（秒）。取 150s 的两条约束：
 * ① 不得大于后端扫码支付锁的 TTL（ISqbOrderPayServiceImpl.SCAN_PAY_LOCK_MINUTES = 10 分钟）——
 *    窗口先到点而锁还在，收银员想重扫却被锁挡住；
 * ② 必须盖住后端补查阶梯 —— pending 落地时距发起已约 60s（SCAN_REQUEST_BUDGET_MS），
 *    两跳补查（间隔 +30s / +60s）落在 pending 落地后 +30s / +90s（即发起后约 +90s / +150s）；
 *    窗口从 pending 回包起算，留 60s 余量；到点后后端已有最终结论（收敛落库或自动撤单），收银员可放心关闭。
 */
const PAY_PENDING_WINDOW_SECONDS = 150;
/** pending 期间两次查单之间的间隔（秒） */
const PAY_PENDING_POLL_INTERVAL_SECONDS = 10;

/** 付费挂号收款状态机的初始值（resetOnsiteForm 与 data 共用同一份形状） */
function freshOnsitePay() {
    return {
        step: 'form', // form 表单 | charge 费用确认 | scan 扫码收款
        feeMode: 'doctor', // doctor 按医生设置 | custom 自定义金额
        customAmount: '',
        targetSchId: null,
        targetReTimeType: '',
        reId: null,
        reSerialNo: '',
        amount: '', // 应收挂号费（元）
        patientName: '',
        doctorName: '',
        isWaitPayLoading: true, // 等待扫码
        payLoading: false, // 已扫码，等待支付结果
        isPayed: false, // 收款流程已出结果（三态之一）
        successPayed: false,
        payState: '', // '' 未发起 / success / pending（结果确认中）/ failed
        payChannel: '',
        payNeedsReview: false, // 外部已收款、本地待核查：只读查单，禁止重扫
        payReasonCode: '',
        payReviewMsg: '',
        payRequesting: false, // 支付请求在途（防止重复扫码发起第二个请求）
        querying: false, // 「重新查询」在途（手动与自动轮询共用，只作并发去重）
        manualQuerying: false, // 手动「重新查询」在途：只有它会锁弹窗，自动轮询不锁
        cancelRequesting: false, // 取消未付单在途，防重复取消
        payPendingTimer: null,
        payPendingRemain: 0,
        payPendingExpired: false,
        payPendingReopened: false,
        payErrorMsg: '',
        queueSeq: null, // 收款成功后分配的排队号
        submitting: false
    };
}

export default {
    data() {
        return {
            currentPage: 1,
            pageNums: 20,
            totalCount: 0,
            queryParams: {
                cardPatientName: '',
                cardPatientPhone: '',
                reSerialNo: '',
                reStatus: '',
                isFree: ''
            },
            tableLoading: false,
            tableData: [],
            listQuerying: false, // 列表行「查询收款结果」进行中
            tableHeight: 500,
            tencentCosUrl: baseConfig.tencentCosUrl,
            doctorInfo: null,
            statData: {
                total: 0,
                waitSign: 0,
                waitVisit: 0,
                visiting: 0,
                done: 0,
                cancel: 0,
                waitPay: 0,
                jumpCall: 0
            },
            queueData: [],
            queueLoading: false,
            // 现场挂号弹窗：医生 + 场次 + 现场填写的患者信息
            onsiteDialog: {
                visible: false,
                submitting: false
            },
            onsiteLoading: false,
            onsiteSlots: [],
            onsiteForm: {
                doctorId: null,
                slotKey: '',
                cardId: null,
                cardPatientName: '',
                cardIdentity: '',
                cardPatientPhone: '',
                // 匹配确认后的落档方式：reuse 复用已有档案 | create 强制新建 | update 更新已有档案再用（null=未弹过窗）
                patientAction: null
            },
            // 手填身份证匹配确认弹窗：matchedList 为后端 onsite-patient-match 回传的全部候选档案
            // （同一人在多个小程序账号名下可能各有一条档案），selectedId 为单选选中的档案（默认最早建档）
            onsiteMatch: {
                visible: false,
                checking: false,
                matchedList: [],
                selectedId: null
            },
            // 付费挂号收款状态机（形状见 freshOnsitePay）
            onsitePay: freshOnsitePay(),
            onsitePayEpoch: 0, // 关闭/切单/新扫码立即令上一轮异步回调失效
            onsitePendingOrders: {}, // POST发出即按订单保留未决态，重开只能只读补查
            // 扫码枪监听器的移除函数（挂载时注册，卸载时清理）
            removeOnsiteScanListener: null,
            // 已有患者搜索：关键字 + 下拉选项与展开状态 + 防抖计时器 + 请求序号（防旧响应覆盖新结果）
            onsitePatientKeyword: '',
            onsitePatientOptions: [],
            onsitePatientDropdownShow: false,
            onsitePatientSearchTimer: null,
            onsitePatientSearchSeq: 0,
            onsiteBaseRules: {
                doctorId: [{required: true, message: '请选择医生', trigger: 'change'}],
                cardPatientName: [{required: true, message: '请填写患者姓名', trigger: 'blur'}],
                cardIdentity: [
                    {required: true, message: '请填写身份证号', trigger: 'blur'},
                    // 与小程序建档同一口径
                    {
                        pattern: /^[1-9][0-9]{5}(19|20)[0-9]{2}((01|03|05|07|08|10|12)(0[1-9]|[1-2][0-9]|3[0-1])|(04|06|09|11)(0[1-9]|[1-2][0-9]|30)|02(0[1-9]|[1-2][0-9]))[0-9]{3}([0-9]|x|X)$/,
                        message: '身份证号格式不正确',
                        trigger: 'blur'
                    }
                ],
                cardPatientPhone: [
                    {required: true, message: '请填写手机号', trigger: 'blur'},
                    {pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur'}
                ]
            },
        }
    },
    watch: {
        'onsiteDialog.visible': {
            flush: 'sync',
            handler(visible) {
                this.invalidateOnsitePaySession();
                if (visible) this.resumeOnsitePayPending();
            }
        },
        'onsitePay.reId': {
            flush: 'sync',
            handler() { this.resetOnsitePaySelection(); }
        },
        // 身份证一改，之前弹窗确认的落档方式（reuse/create/update）就作废了：
        // 换人换号后匹配结果完全不同，必须重新预检查，不能带着残留的 patientAction 跳过弹窗
        'onsiteForm.cardIdentity'() {
            this.onsiteForm.patientAction = null;
            this._onsiteChosenCardId = null;
        },
    },
    computed: {
        // 匹配确认弹窗当前选中的候选档案（差异提醒和提交都以它为准，找不到返回 null）
        onsiteMatchSelected() {
            return (this.onsiteMatch.matchedList || []).find(item => item.patientId === this.onsiteMatch.selectedId) || null;
        },
        // 当天排班里可挂号的医生（去重）
        onsiteDoctors() {
            const doctors = [];
            this.onsiteSlots.forEach(slot => {
                if (!doctors.some(item => item.doctorId === slot.doctorId)) {
                    doctors.push({doctorId: slot.doctorId, docName: slot.docName, odName: slot.odName});
                }
            });
            return doctors;
        },
        // 选中医生的预约费（元，来自后端 onsite-schedules 快照）；未设置为 null（只能走自定义金额）
        onsiteDoctorFee() {
            const slot = this.onsiteDoctorSlots[0];
            if (!slot) return null;
            const fee = parseFloat(slot.docRePrice);
            return isNaN(fee) ? null : fee;
        },
        // 收款结果面板主文案：与后端三态契约一致，pending 不得渲染成失败
        onsitePayResultText() {
            const pay = this.onsitePay;
            if (pay.payState === 'success') return '已支付成功';
            if (pay.payState === 'pending') {
                if (pay.payNeedsReview) return this.onsiteReviewStatusText(pay.payReasonCode);
                return pay.payPendingExpired ? '待人工核查' : '支付结果确认中';
            }
            return '支付失败';
        },
        // 只锁「请求在途」：支付请求受 axios 70s 超时封顶、手动重查是单次查单耗时，故不可关闭时长上限≈70s。
        // pending 期间放行关闭（结果未定时关窗确实会诱导重扫，但收银员不能被关在只能干等的弹窗里）——
        // 关窗后仍有列表「查询收款结果」、服务端补查阶梯与日终对账三层兜底。
        // 用 manualQuerying 而不是 querying：后者被 10s 自动轮询共用，直接锁会让弹窗每 10 秒闪一下。
        onsitePayDialogLocked() {
            const pay = this.onsitePay;
            return pay.payRequesting || pay.manualQuerying;
        },
        // 选中医生当天排了号源的场次
        onsiteDoctorSlots() {
            return this.onsiteSlots.filter(slot => slot.doctorId === this.onsiteForm.doctorId);
        },
        // 费用确认页展示的医生名（与下拉框同格式）
        onsiteDoctorLabel() {
            const doc = this.onsiteDoctors.find(item => item.doctorId === this.onsiteForm.doctorId);
            return doc ? doc.docName + (doc.odName ? '（' + doc.odName + '）' : '') : '—';
        },
        // 还能挂的场次：号源没占满、且号源时间在当前之后
        onsiteOpenSlots() {
            return this.onsiteDoctorSlots.filter(slot => slot.open);
        },
        // 场次必填只在有可挂号源时生效：都约满/已结束时不用选，直接排到最后
        onsiteRules() {
            const rules = Object.assign({}, this.onsiteBaseRules);
            if (this.onsiteOpenSlots.length > 0) {
                rules.slotKey = [{required: true, message: '请选择场次', trigger: 'change'}];
            }
            // 已选已有档案：姓名/身份证/手机号以档案为准，后端会忽略表单快照，不再校验
            if (this.onsiteForm.cardId) {
                delete rules.cardPatientName;
                delete rules.cardIdentity;
                delete rules.cardPatientPhone;
            }
            return rules;
        },
        todayLabel() {
            const date = new Date();
            const m = date.getMonth() + 1;
            const d = date.getDate();
            const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
            const w = weekDays[date.getDay()];
            return `${m}月${d}日 星期${w}`;
        },
    },
    mounted() {
        setTimeout(() => {
            if (this.$refs.multipleTable && this.$refs.multipleTable.$el) {
                this.tableHeight = window.innerHeight - this.$refs.multipleTable.$el.offsetTop - 80;
            }
        }, 1000);
        // 扫码枪监听常驻：真正是否处理由门卫条件（弹窗显示 + scan 步骤 + 等待扫码）决定
        this.initOnsiteScanListener();
    },
    beforeUnmount() {
        this.invalidateOnsitePaySession();
        if (this.removeOnsiteScanListener) {
            this.removeOnsiteScanListener();
        }
    },
    created() {
        this.doctorInfo = JSON.parse(window.sessionStorage.getItem("doctorInfo"));
        this.getTodayReservationStatInfo();
        this.getTodayReservationListInfo();
        this.getUnifiedQueueInfo();
    },
    methods: {
        captureOnsitePaySession() {
            return {pay: this.onsitePay, epoch: this.onsitePayEpoch,
                reId: this.onsitePay.reId, visible: this.onsiteDialog.visible};
        },

        isCurrentOnsitePaySession(session) {
            return !!session && this.onsitePay === session.pay && session.epoch === this.onsitePayEpoch
                && session.reId === this.onsitePay.reId && session.visible === this.onsiteDialog.visible;
        },

        invalidateOnsitePaySession() {
            this.onsitePayEpoch++;
            this.stopOnsitePayPendingWatch();
            const pay = this.onsitePay;
            pay.payRequesting = false;
            pay.querying = false;
            pay.manualQuerying = false;
            pay.cancelRequesting = false;
            pay.submitting = false;
            this.onsiteMatch.checking = false;
            this.listQuerying = false;
        },

        resetOnsitePaySelection() {
            this.invalidateOnsitePaySession();
            const pay = this.onsitePay;
            pay.isWaitPayLoading = true;
            pay.payLoading = false;
            pay.isPayed = false;
            pay.successPayed = false;
            pay.payState = '';
            pay.payChannel = '';
            pay.payErrorMsg = '';
            pay.payNeedsReview = false;
            pay.payReasonCode = '';
            pay.payReviewMsg = '';
            pay.payPendingExpired = false;
            pay.payPendingRemain = 0;
            this.resumeOnsitePayPending();
        },

        resumeOnsitePayPending() {
            const pay = this.onsitePay;
            const pending = this.onsitePendingOrders[pay.reId];
            if (!this.onsiteDialog.visible || !pending) return;
            pay.step = 'scan';
            pay.payNeedsReview = pending.needsReview;
            pay.payReasonCode = pending.reasonCode;
            pay.payReviewMsg = pending.msg || '';
            pay.isWaitPayLoading = false;
            pay.isPayed = true;
            pay.payState = 'pending';
            pay.payPendingExpired = true;
            pay.payPendingReopened = true;
            pay.payErrorMsg = this.onsitePendingExpiredMessage();
            this.requeryOnsitePayResult(true);
        },

        isOnsitePayNetworkError(error) {
            return !!error && !error.response && (error.request
                || ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error.code)
                || error.message === 'Network Error');
        },

        getUnifiedQueueInfo() {
            this.queueLoading = true;
            //收费端不绑定医生：不传 doctorId，后端返回当天全部医生的队列（条目带 doctorName）
            getUnifiedClinicQueue({schDate: getTodayDate()})
                .then(res => {
                    const payload = res.data || res;
                    this.queueData = payload.code === 200 ? (payload.data || []) : [];
                })
                .finally(() => { this.queueLoading = false; });
        },

        // 打开现场挂号弹窗：每次重新取当天排班，避免用到过期的场次
        openOnsiteDialog() {
            this.onsiteForm.doctorId = this.doctorInfo && this.doctorInfo.doctorId ? this.doctorInfo.doctorId : null;
            this.onsiteDialog.visible = true;
            this.loadOnsiteSchedules();
        },

        loadOnsiteSchedules() {
            this.onsiteLoading = true;
            getOnsiteSchedules({schDate: getTodayDate()}).then(res => {
                const payload = res.data || res;
                const rows = payload.code === 200 ? (payload.data || []) : [];
                this.onsiteSlots = this.buildOnsiteSlots(rows);
                if (payload.code !== 200) {
                    message(payload.msg || '排班加载失败', 'warning');
                }
                // 预选的当前登录医生若当天没有排班，清掉避免下拉框显示无效值
                if (!this.onsiteSlots.some(slot => slot.doctorId === this.onsiteForm.doctorId)) {
                    this.onsiteForm.doctorId = null;
                }
                if (!this.onsiteSlots.some(slot => slot.key === this.onsiteForm.slotKey)) {
                    this.onsiteForm.slotKey = '';
                }
            }).catch(error => {
                this.onsiteSlots = [];
                message((error && error.message) || '排班加载失败，请稍后重试', 'error');
            }).finally(() => { this.onsiteLoading = false; });
        },

        // 一个排班行含上午/下午/夜间三个场次，后端已按号源情况过滤并算好空号源，这里只做展示组装
        buildOnsiteSlots(rows) {
            const slots = [];
            rows.forEach(row => {
                if (!row.schId || !row.doctorId) return;
                (row.slots || []).forEach(slot => {
                    const start = slot.startTime ? slot.startTime.substring(0, 5) : '';
                    const end = slot.endTime ? slot.endTime.substring(0, 5) : '';
                    const timeText = start ? ' ' + start + (end ? '-' + end : '') : '';
                    slots.push({
                        key: row.schId + '|' + slot.reTimeType,
                        schId: row.schId,
                        reTimeType: slot.reTimeType,
                        doctorId: row.doctorId,
                        docName: row.docName,
                        odName: row.odName,
                        // 医生设置的预约费快照（付费挂号「按医生设置」用）；未设置为 null
                        docRePrice: row.docRePrice == null ? null : row.docRePrice,
                        // open=还有空号源，才能占号挂号；否则只能排到最后
                        open: !!slot.open,
                        freeNum: slot.freeNum || 0,
                        label: (slot.label || '') + timeText + (slot.open ? '（余' + (slot.freeNum || 0) + '）' : '（已约满）')
                    });
                });
            });
            return slots;
        },

        handleOnsiteDoctorChange() {
            this.onsiteForm.slotKey = '';
        },

        // 已有患者搜索：输入防抖 300ms，关键字为空时立即收起下拉
        handleOnsitePatientInput(value) {
            const kw = (value || '').trim();
            if (this.onsitePatientSearchTimer) {
                clearTimeout(this.onsitePatientSearchTimer);
                this.onsitePatientSearchTimer = null;
            }
            if (!kw) {
                this.onsitePatientSearchSeq++;
                this.onsitePatientOptions = [];
                this.onsitePatientDropdownShow = false;
                return;
            }
            this.onsitePatientSearchTimer = setTimeout(() => this.searchOnsitePatient(kw), 300);
        },

        searchOnsitePatient(kw) {
            const seq = ++this.onsitePatientSearchSeq;
            searchReservationPatients(kw).then(res => {
                // 只认最后一次搜索的结果，避免响应乱序时旧结果覆盖新结果
                if (seq !== this.onsitePatientSearchSeq) return;
                // getRequest resolve 的是 axios response，payload 在 res.data
                const payload = res.data || res;
                const rows = payload.code === 200 ? (payload.data || []) : [];
                this.onsitePatientOptions = rows.map(item => ({
                    ...item,
                    sexText: item.sex === '0' ? '男' : item.sex === '1' ? '女' : '未知'
                }));
                this.onsitePatientDropdownShow = this.onsitePatientOptions.length > 0;
            }).catch(() => {
                if (seq !== this.onsitePatientSearchSeq) return;
                this.onsitePatientDropdownShow = false;
            });
        },

        // 选中已有档案：回填 cardId + 快照字段，输入框锁定，挂号信息以档案为准
        chooseOnsitePatient(item) {
            if (this.onsitePatientSearchTimer) {
                clearTimeout(this.onsitePatientSearchTimer);
                this.onsitePatientSearchTimer = null;
            }
            this.onsitePatientSearchSeq++;
            this.onsiteForm.cardId = item.patientId;
            this.onsiteForm.cardPatientName = item.name || '';
            this.onsiteForm.cardIdentity = item.identity || '';
            this.onsiteForm.cardPatientPhone = item.phoneNumber || '';
            // 已选档案时后端直接复用，匹配确认的落档方式不再生效，清掉避免残留到下次手填
            this.onsiteForm.patientAction = null;
            this._onsiteChosenCardId = null;
            this.onsitePatientDropdownShow = false;
            this.onsitePatientKeyword = item.name + '（已选档案，信息以档案为准）';
            this.$nextTick(() => {
                if (this.$refs.onsiteFormRef) {
                    this.$refs.onsiteFormRef.clearValidate();
                }
            });
        },

        // blur 延迟 200ms 关闭，配合选项上的 @mousedown.prevent，保证点选项时下拉还在
        handleOnsitePatientBlur() {
            setTimeout(() => { this.onsitePatientDropdownShow = false; }, 200);
        },

        handleOnsitePatientFocus() {
            if (this.onsitePatientOptions.length > 0) {
                this.onsitePatientDropdownShow = true;
            }
        },

        // 清除已选档案：回到纯手填模式，cardId 置空后后端走身份证匹配/建档逻辑
        clearOnsitePatient() {
            if (this.onsitePatientSearchTimer) {
                clearTimeout(this.onsitePatientSearchTimer);
                this.onsitePatientSearchTimer = null;
            }
            this.onsitePatientSearchSeq++;
            this.onsiteForm.cardId = null;
            this.onsiteForm.cardPatientName = '';
            this.onsiteForm.cardIdentity = '';
            this.onsiteForm.cardPatientPhone = '';
            // 回到纯手填模式：清掉上一次匹配确认留下的落档方式和选中档案
            this.onsiteForm.patientAction = null;
            this._onsiteChosenCardId = null;
            this.onsitePatientKeyword = '';
            this.onsitePatientOptions = [];
            this.onsitePatientDropdownShow = false;
            if (this.$refs.onsiteFormRef) {
                this.$refs.onsiteFormRef.clearValidate();
            }
        },

        // 表单校验 + 场次解析（免费/付费共用）。通过后回调 (target, hasSlot)：
        // target = 选中的排班场次（无可挂号源时取该医生第一行），hasSlot = 是否明确选了场次
        validateOnsiteForm(callback) {
            const formRef = this.$refs.onsiteFormRef;
            if (!formRef) return;
            formRef.validate(valid => {
                if (!valid) return;
                const openSlots = this.onsiteOpenSlots;
                const slot = openSlots.find(item => item.key === this.onsiteForm.slotKey);
                // 有可挂号源必须选场次（现场占的是该场次的线上号源）；
                // 一个可挂号源都没有时不用选，后端会把患者排到最后、接着线上编号取号
                if (openSlots.length > 0 && !slot) {
                    message('请选择挂号场次', 'warning');
                    return;
                }
                const target = slot || this.onsiteDoctorSlots[0];
                if (!target) {
                    message('请选择医生', 'warning');
                    return;
                }
                callback(target, !!slot);
            });
        },

        // 免费挂号：表单校验 + 手填身份证匹配预检查，确认落档方式后再提交
        submitOnsite() {
            this.validateOnsiteForm((target, hasSlot) => {
                this.checkOnsitePatientMatch(() => {
                    this.onsiteDialog.submitting = true;
                    // 现场挂号与线上共用号序：带 cardId 直接复用（快照以档案为准）；
                    // cardId 优先用匹配弹窗选中的档案，其次表单搜索已选的档案；
                    // 都没有时后端按身份证 + patientAction 处理档案（复用/强制新建/更新后再用）
                    const request = {
                        schId: target.schId,
                        reTimeType: hasSlot ? target.reTimeType : '',
                        cardId: this._onsiteChosenCardId || this.onsiteForm.cardId || undefined,
                        cardPatientName: this.onsiteForm.cardPatientName.trim(),
                        cardIdentity: this.onsiteForm.cardIdentity.trim(),
                        cardPatientPhone: this.onsiteForm.cardPatientPhone.trim(),
                        patientAction: this.onsiteForm.patientAction || undefined
                    };
                    // 弹窗选中的档案只对本次提交生效
                    this._onsiteChosenCardId = null;
                    registerOnsiteFree(request).then(res => {
                        if (res.code === 200) {
                            // 复用档案时姓名以档案为准，提示里用后端回传的最终姓名与排队号
                            const name = res.cardPatientName || this.onsiteForm.cardPatientName.trim();
                            const noText = res.queueSeq ? '排队' + res.queueSeq + '号' : '';
                            message('现场挂号成功：' + name + (noText ? '（' + noText + '）' : ''), 'success');
                            this.onsiteDialog.visible = false;
                            this.getTodayReservationListInfo();
                            this.getTodayReservationStatInfo();
                            this.getUnifiedQueueInfo();
                        } else {
                            message(res.msg || '现场挂号失败', 'warning');
                        }
                    }).catch(error => {
                        message((error && error.message) || '现场挂号失败，请稍后重试', 'error');
                    }).finally(() => { this.onsiteDialog.submitting = false; });
                });
            });
        },

        // 付费挂号第一步：表单校验 + 手填身份证匹配预检查，确认落档方式后进入费用确认页
        startOnsiteCharge() {
            this.validateOnsiteForm((target, hasSlot) => {
                this.checkOnsitePatientMatch(() => {
                    const pay = this.onsitePay;
                    pay.targetSchId = target.schId;
                    pay.targetReTimeType = hasSlot ? target.reTimeType : '';
                    // 医生没设置预约费时只能走自定义金额
                    pay.feeMode = this.onsiteDoctorFee && this.onsiteDoctorFee > 0 ? 'doctor' : 'custom';
                    pay.step = 'charge';
                });
            });
        },

        // 手填身份证预检查：匹配到已有档案先弹确认窗，操作员选中具体档案（或强制新建）后再续跑被挂起的提交。
        // 已选档案（cardId）或身份证为空时不拦截直接续跑（后者由后端校验兜底）
        checkOnsitePatientMatch(proceed) {
            const identity = (this.onsiteForm.cardIdentity || '').trim();
            if (this.onsiteForm.cardId || !identity) {
                proceed();
                return;
            }
            if (this.onsiteMatch.checking) {
                return;
            }
            this.onsiteMatch.checking = true;
            // 过期响应防护：主弹窗关闭时 resetOnsiteForm 会换新 onsitePay 对象，
            // 回包到达时若对象已换，说明弹窗已关闭重置，不得再弹确认窗、不得再续跑提交（同 confirmOnsiteCharge 口径）
            const session = this.captureOnsitePaySession();
            matchReservationPatient(identity).then(res => {
                if (!this.isCurrentOnsitePaySession(session)) {
                    return;
                }
                const payload = res.data || res;
                const matchedList = payload.code === 200 ? (payload.data || []) : [];
                if (!matchedList.length) {
                    proceed();
                    return;
                }
                this.onsiteMatch.matchedList = matchedList;
                // 默认选中最早建档的一条（后端已按建档时间升序）
                this.onsiteMatch.selectedId = matchedList[0].patientId;
                // 续跑闭包挂非响应式实例属性，避免被 Vue 响应式代理包装
                this._onsiteMatchProceed = proceed;
                this.onsiteMatch.visible = true;
            }).catch(error => {
                if (!this.isCurrentOnsitePaySession(session)) {
                    return;
                }
                // 预检查失败不挡挂号：按旧行为直接提交，落档口径由后端 resolveOnsitePatient 兜底
                message((error && error.message) || '患者档案匹配检查失败，将按匹配/建档逻辑直接提交', 'warning');
                proceed();
            }).finally(() => {
                if (this.isCurrentOnsitePaySession(session)) this.onsiteMatch.checking = false;
            });
        },

        // 匹配确认弹窗操作：reuse/update=把选中档案作为本次挂号的 cardId（update 另带写回标记），
        // create=不带 cardId 强制新建。选中档案挂非响应式实例属性，避免污染表单的「已选档案」状态
        chooseOnsiteMatchAction(action) {
            const selectedId = this.onsiteMatch.selectedId;
            this.onsiteMatch.visible = false;
            this.onsiteMatch.matchedList = [];
            this.onsiteMatch.selectedId = null;
            const proceed = this._onsiteMatchProceed;
            this._onsiteMatchProceed = null;
            if (!proceed) {
                return;
            }
            if (action === 'create') {
                this._onsiteChosenCardId = null;
                this.onsiteForm.patientAction = 'create';
            } else {
                this._onsiteChosenCardId = selectedId;
                this.onsiteForm.patientAction = action === 'update' ? 'update' : null;
            }
            proceed();
        },

        // 匹配确认弹窗点取消/关闭：只关弹窗不提交，留在表单页让操作员核对或修改信息
        handleOnsiteMatchClose() {
            this.onsiteMatch.visible = false;
            this.onsiteMatch.matchedList = [];
            this.onsiteMatch.selectedId = null;
            this._onsiteMatchProceed = null;
        },

        // 付费挂号第二步：确认金额 → 创建待支付挂号单 → 进入扫码收款
        confirmOnsiteCharge() {
            const pay = this.onsitePay;
            if (!this.onsiteDialog.visible || pay.step !== 'charge' || pay.submitting) {
                return;
            }
            let amount;
            if (pay.feeMode === 'doctor') {
                amount = this.onsiteDoctorFee;
                if (!amount || amount <= 0) {
                    message('该医生未设置预约费，请选择自定义金额', 'warning');
                    return;
                }
            } else {
                const raw = (pay.customAmount || '').trim();
                if (!/^\d{1,5}(\.\d{1,2})?$/.test(raw)) {
                    message('请输入正确的金额（0.01 ~ 99999.99，最多两位小数）', 'warning');
                    return;
                }
                amount = parseFloat(raw);
                if (!(amount > 0 && amount <= 99999.99)) {
                    message('请输入正确的金额（0.01 ~ 99999.99）', 'warning');
                    return;
                }
            }
            const session = this.captureOnsitePaySession();
            pay.submitting = true;
            const request = {
                schId: pay.targetSchId,
                reTimeType: pay.targetReTimeType,
                // cardId 优先用匹配弹窗选中的档案，其次表单搜索已选的档案
                cardId: this._onsiteChosenCardId || this.onsiteForm.cardId || undefined,
                cardPatientName: this.onsiteForm.cardPatientName.trim(),
                cardIdentity: this.onsiteForm.cardIdentity.trim(),
                cardPatientPhone: this.onsiteForm.cardPatientPhone.trim(),
                patientAction: this.onsiteForm.patientAction || undefined
            };
            // 弹窗选中的档案只对本次提交生效
            this._onsiteChosenCardId = null;
            // 自定义金额才带 docRePrice；按医生设置时不传，金额以服务端 doctor_info 配置为准
            if (pay.feeMode === 'custom') {
                request.docRePrice = amount.toFixed(2);
            }
            return registerOnsitePaid(request).then(res => {
                // 过期响应防护：回包到达时弹窗可能已重置（resetOnsiteForm 换新对象），旧单回包不得写进新会话
                if (!this.isCurrentOnsitePaySession(session)) {
                    return;
                }
                if (res.code === 200) {
                    pay.reId = res.data;
                    // 本次建单绑定服务端分配的ID；同步watch已使旧建单前会话失效。
                    session.reId = pay.reId;
                    session.epoch = this.onsitePayEpoch;
                    pay.reSerialNo = res.reSerialNo || '';
                    // 应收金额以后端快照为准（按医生设置时服务端重新读取配置）
                    pay.amount = pay.feeMode === 'custom' ? amount.toFixed(2) : (res.docRePrice || amount.toFixed(2));
                    pay.patientName = res.cardPatientName || this.onsiteForm.cardPatientName.trim();
                    const doc = this.onsiteDoctors.find(item => item.doctorId === this.onsiteForm.doctorId);
                    pay.doctorName = doc ? doc.docName : '';
                    pay.isWaitPayLoading = true;
                    pay.step = 'scan';
                } else {
                    message(res.msg || '创建付费挂号单失败', 'warning');
                }
            }).catch(error => {
                if (this.isCurrentOnsitePaySession(session)) {
                    message((error && error.message) || '创建付费挂号单失败，请稍后重试', 'error');
                }
            }).finally(() => {
                if (this.isCurrentOnsitePaySession(session)) pay.submitting = false;
            });
        },

        // 扫码枪扫描处理方法
        handleOnsiteScanCode(code) {
            if (this.onsiteDialog.visible && this.onsitePay.step === 'scan' && this.onsitePay.isWaitPayLoading) {
                this.processOnsitePaymentCode(code);
            }
        },

        // 处理付款码：发起收钱吧付款码支付（B扫C）
        processOnsitePaymentCode(dynamicId) {
            const pay = this.onsitePay;
            if (!dynamicId) {
                message("扫描的条码无效", "error");
                return;
            }
            if (!this.onsiteDialog.visible || pay.step !== 'scan' || !pay.reId
                || pay.payRequesting || pay.querying || pay.cancelRequesting
                || pay.payState === 'pending' || pay.payState === 'success' || pay.payNeedsReview
                || this.onsitePendingOrders[pay.reId]) return;
            this.invalidateOnsitePaySession();
            const session = this.captureOnsitePaySession();
            this.onsitePendingOrders[session.reId] = {needsReview: false, reasonCode: '', msg: ''};
            pay.payRequesting = true;
            pay.payPendingExpired = false;
            pay.payPendingRemain = 0;
            pay.payPendingReopened = false;
            pay.isWaitPayLoading = false;
            pay.payLoading = true;
            pay.isPayed = false;
            pay.payState = "";
            pay.payChannel = "";
            pay.payErrorMsg = "";
            const params = {reId: session.reId, price: pay.amount, dynamicId: dynamicId};
            return clinicSqbReservationPay(JSON.stringify(params)).then(response => {
                if (!this.isCurrentOnsitePaySession(session)) return;
                this.applyOnsitePayResult(response, false, session);
            }).catch(error => {
                if (!this.isCurrentOnsitePaySession(session)) return;
                console.error('支付请求失败:', error);
                const result = this.isOnsitePayNetworkError(error)
                    ? {code: 601, data: {payState: 'pending'}}
                    : ((error && error.response && error.response.data) || {code: 500, msg: '支付请求失败'});
                this.applyOnsitePayResult(result, false, session);
            }).finally(() => {
                if (this.isCurrentOnsitePaySession(session)) pay.payRequesting = false;
            });
        },

        /**
         * 按后端三态契约渲染收款结果：
         *   code=200 + data.payState=success + data.queueSeq → 已收款，患者已进队列
         *   code=601 + data.payState=pending                 → 结果确认中，既非成功也非失败
         *   其余                                             → 明确失败
         */
        applyOnsitePayResult(response, silent, session = this.captureOnsitePaySession()) {
            if (!this.isCurrentOnsitePaySession(session) || this.onsitePay.payState === 'success') return;
            const pay = this.onsitePay;
            // POST resolve 的是 response.data（带 code/msg/data），GET resolve 的是完整 axios response，先归一化
            const payload = (response && response.data && response.data.code !== undefined) ? response.data : response;
            const data = (payload && payload.data) || {};
            pay.isWaitPayLoading = false;
            pay.payLoading = false;
            pay.isPayed = true;
            pay.payErrorMsg = "";
            if (payload && payload.code === 200 && data.payState === 'success') {
                pay.payState = 'success';
                delete this.onsitePendingOrders[session.reId];
                pay.payRequesting = false;
                pay.querying = false;
                pay.manualQuerying = false;
                pay.payNeedsReview = false;
                pay.payReasonCode = '';
                pay.payReviewMsg = '';
                pay.payChannel = data.channel || 'SQB';
                pay.queueSeq = data.queueSeq || null;
                pay.successPayed = true;
                this.stopOnsitePayPendingWatch();
                pay.payPendingExpired = false;
                message(pay.queueSeq ? ('挂号费收款成功，排队' + pay.queueSeq + '号') : '挂号费收款成功', 'success');
                this.getTodayReservationListInfo();
                this.getTodayReservationStatInfo();
                this.getUnifiedQueueInfo();
            } else if ((payload && payload.code === 601 && data.payState === 'pending') || pay.payNeedsReview) {
                if (data.externalPayState === 'PAID' && data.localPostingState === 'needsReview') {
                    pay.payNeedsReview = true;
                    pay.payReasonCode = data.reasonCode || pay.payReasonCode;
                    pay.payReviewMsg = payload.msg || pay.payReviewMsg;
                }
                // 第三态：既不得显示成功，也不得显示失败
                pay.payState = 'pending';
                this.onsitePendingOrders[session.reId] = {needsReview: pay.payNeedsReview, reasonCode: pay.payReasonCode, msg: pay.payReviewMsg};
                pay.payChannel = "";
                pay.successPayed = false;
                if (!pay.payPendingTimer && !pay.payPendingExpired) {
                    // 首次进入 pending：开自动轮询窗口（与后端支付锁 TTL 对齐）
                    this.startOnsitePayPendingWatch();
                }
                pay.payErrorMsg = pay.payPendingExpired ? this.onsitePendingExpiredMessage() : this.onsitePendingMessage();
                if (!silent) {
                    message(pay.payNeedsReview ? pay.payErrorMsg : "支付结果确认中，请勿重复扫码", "warning");
                }
            } else {
                pay.payState = 'failed';
                delete this.onsitePendingOrders[session.reId];
                this.stopOnsitePayPendingWatch();
                pay.payPendingExpired = false;
                pay.payChannel = "";
                pay.successPayed = false;
                pay.payErrorMsg = (payload && payload.msg) || "支付失败";
                message(pay.payErrorMsg, "error");
            }
        },

        // pending 态的「重新查询」。走只读查单接口，绝不会发起扣款，可安全重复点击。
        requeryOnsitePayResult(silent) {
            const pay = this.onsitePay;
            const session = this.captureOnsitePaySession();
            if (!this.onsiteDialog.visible || pay.querying || !pay.reId || pay.payState === 'success') {
                return;
            }
            pay.querying = true;
            if (!silent) {
                // 只有手动重查锁弹窗；自动轮询不锁，否则 pending 期间弹窗每 10 秒闪一下
                pay.manualQuerying = true;
            }
            //silent=true 即自动轮询，作为 autoPoll 传给后端区分「机器在等」与「收银员手动查」
            return clinicSqbReservationQueryResult(session.reId, !!silent).then(response => {
                // 过期响应防护：回包到达时弹窗可能已重置（resetOnsiteForm 换新对象），旧回包不得写进新会话
                if (!this.isCurrentOnsitePaySession(session)) {
                    return;
                }
                this.applyOnsitePayResult(response, silent, session);
            }).catch(error => {
                if (!this.isCurrentOnsitePaySession(session) || pay.payState === 'success') {
                    return;
                }
                console.error('重新查询支付结果失败:', error);
                if (!silent) {
                    message("重新查询失败，请稍后重试", "error");
                }
            }).finally(() => {
                if (!this.isCurrentOnsitePaySession(session)) return;
                pay.querying = false;
                pay.manualQuerying = false;
            });
        },

        // 列表行「查询收款结果」：弹窗关闭后仍可从这里补查。只读查单，绝不发起扣款。
        // autoPoll 传 false：后端据此把留痕记成 manual（只有弹窗自动轮询才传 true）。
        queryListPayResult(row) {
            if (this.listQuerying || !row.reId) {
                return;
            }
            const session = this.captureOnsitePaySession();
            const reId = row.reId;
            this.listQuerying = true;
            return clinicSqbReservationQueryResult(reId, false).then(response => {
                if (!this.isCurrentOnsitePaySession(session) || row.reId !== reId) return;
                const payload = (response && response.data && response.data.code !== undefined) ? response.data : response;
                const data = (payload && payload.data) || {};
                if (payload && payload.code === 200 && data.payState === 'success') {
                    delete this.onsitePendingOrders[reId];
                    message("支付成功", "success");
                    this.getTodayReservationListInfo();
                    this.getTodayReservationStatInfo();
                    this.getUnifiedQueueInfo();
                } else if (payload && payload.code === 601 && data.payState === 'pending') {
                    const previous = this.onsitePendingOrders[reId] || {};
                    this.onsitePendingOrders[reId] = {
                        needsReview: previous.needsReview || (data.externalPayState === 'PAID' && data.localPostingState === 'needsReview'),
                        reasonCode: data.reasonCode || previous.reasonCode || '', msg: payload.msg || previous.msg || ''
                    };
                    //601 是正常业务态，绝不能报 error —— 报错会诱导收银员重扫
                    message(data.externalPayState === 'PAID' && data.localPostingState === 'needsReview'
                        ? this.onsiteReviewStatusText(data.reasonCode) + (payload.msg ? '，' + payload.msg : '')
                        : "收款结果仍未确认，请稍后再查", "warning");
                } else {
                    //含「该订单尚未发起过扫码支付，无法查询支付结果」，原样透出后端文案
                    message((payload && payload.msg) || "查询收款结果失败", "error");
                }
            }).catch(error => {
                if (!this.isCurrentOnsitePaySession(session) || row.reId !== reId) return;
                console.error('查询收款结果失败:', error);
                message(errorMsg(error, "查询收款结果失败"), "error");
            }).finally(() => {
                if (this.isCurrentOnsitePaySession(session)) this.listQuerying = false;
            });
        },

        /**
         * 开始 pending 自动轮询：每 10 秒查一次只读查单接口，最长 PAY_PENDING_WINDOW_SECONDS 秒；
         * 到点转「待人工核查」。
         * 窗口到点后不再放行重扫（界面只保留「重新查询/取消」）：后端支付锁虽已过期，
         * 但挂号单仍在 10 分钟超时关单（自建单起算）的保护期内 —— 此时重查仍可能收敛成成功，
         * 患者也仍能进队列；若窗口到点时患者实际已付，由服务端补查收敛或自动撤单退款兜住。
         * 重查已关闭的单会给「请重新挂号」提示。
         */
        startOnsitePayPendingWatch() {
            this.stopOnsitePayPendingWatch();
            this.onsitePay.payPendingExpired = false;
            this.onsitePay.payPendingRemain = PAY_PENDING_WINDOW_SECONDS;
            const session = this.captureOnsitePaySession();
            const pay = session.pay;
            const timer = setInterval(() => {
                if (!this.isCurrentOnsitePaySession(session)) {
                    clearInterval(timer);
                    return;
                }
                // 状态已被其他路径改写（成功/失败/重置）时窗口自行结束
                if (pay.payState !== 'pending') {
                    this.stopOnsitePayPendingWatch();
                    return;
                }
                pay.payPendingRemain -= 1;
                if (pay.payPendingRemain <= 0) {
                    this.stopOnsitePayPendingWatch();
                    this.markOnsitePayPendingExpired();
                    return;
                }
                pay.payErrorMsg = this.onsitePendingMessage();
                if (pay.payPendingRemain % PAY_PENDING_POLL_INTERVAL_SECONDS === 0) {
                    // 自动轮询不弹 toast，避免每 10 秒打扰收银员一次
                    this.requeryOnsitePayResult(true);
                }
            }, 1000);
            pay.payPendingTimer = timer;
        },

        stopOnsitePayPendingWatch() {
            if (this.onsitePay.payPendingTimer) {
                clearInterval(this.onsitePay.payPendingTimer);
                this.onsitePay.payPendingTimer = null;
            }
        },

        // 窗口到点：转「待人工核查」。绝不能说成「支付失败」—— 钱可能已经收了。
        markOnsitePayPendingExpired() {
            this.onsitePay.payPendingExpired = true;
            this.onsitePay.payErrorMsg = this.onsitePendingExpiredMessage();
            message(this.onsitePay.payErrorMsg, "warning");
        },

        onsiteReviewStatusText(reasonCode) {
            if (reasonCode === 'LATE_REFUND') return '已收款，已登记自动退款，禁止重扫';
            if (reasonCode === 'QUEUE_ASSIGNING') return '已收款，排队号分配中，禁止重扫';
            return '已收款，本地入账待核查，禁止重扫';
        },

        onsiteReviewMessage(expired) {
            const pay = this.onsitePay;
            let action = '（只读查询中）';
            if (expired) {
                action = pay.payReasonCode === 'LATE_REFUND' ? '，请人工核对退款'
                    : pay.payReasonCode === 'QUEUE_ASSIGNING' ? '，请人工核查排队号分配' : '，请人工核账';
            }
            return this.onsiteReviewStatusText(pay.payReasonCode) + action
                + (pay.payReviewMsg ? '，' + pay.payReviewMsg : '')
                + (pay.payReasonCode ? '，原因：' + pay.payReasonCode : '');
        },

        onsitePendingMessage() {
            const pay = this.onsitePay;
            if (pay.payNeedsReview) return this.onsiteReviewMessage(false);
            return "支付结果确认中，请勿重复扫码（自动查询中，剩余 " + this.onsitePay.payPendingRemain + " 秒）";
        },

        onsitePendingExpiredMessage() {
            const pay = this.onsitePay;
            if (pay.payNeedsReview) return this.onsiteReviewMessage(true);
            if (pay.payPendingReopened) return "支付结果仍未确认，请只读查询收款结果，禁止重复扫码";
            return "支付结果超过 " + PAY_PENDING_WINDOW_SECONDS + " 秒仍未确认，请人工核查收钱吧流水";
        },

        // 支付失败后回到等待扫码（重新收款）
        onsiteRetryScan() {
            const pay = this.onsitePay;
            if (!this.onsiteDialog.visible || pay.payState !== 'failed' || pay.payNeedsReview || this.onsitePayDialogLocked) return;
            this.invalidateOnsitePaySession();
            pay.isWaitPayLoading = true;
            pay.payLoading = false;
            pay.isPayed = false;
            pay.successPayed = false;
            pay.payState = "";
            pay.payChannel = "";
            pay.payErrorMsg = "";
            pay.payPendingExpired = false;
            pay.payPendingRemain = 0;
        },

        closeOnsiteDialog() {
            this.invalidateOnsitePaySession();
            this.onsiteDialog.visible = false;
        },

        /**
         * 统一关闭入口（el-dialog before-close + footer 取消）：
         * - 表单/费用确认/成功/未建单：直接关
         * - 收款结果确认中（pending）或支付请求在途：**一律不关**，提示「请勿关闭弹窗」——
         *   结果未知时关窗会诱导收银员重扫，而钱可能已经扣了。待 pending 窗口（150s）
         *   到点转「待人工核查」后本拦截自动放开；此时单据仍在 10 分钟超时关单保护期内，
         *   迟到支付由服务端补查收敛或自动撤单退款兜住 —— 绝不在结果未知时取消单据
         * - 未扫码/明确失败：调后台取消未支付单，释放号源
         */
        handleOnsiteDialogClose() {
            const pay = this.onsitePay;
            const session = this.captureOnsitePaySession();
            if (pay.submitting) {
                // 建单请求在途：此时关闭（或先「上一步」再关），回包丢失 = 单据失联占号
                message("正在创建挂号单，请稍候", "warning");
                return;
            }
            if (pay.step === 'scan' && pay.reId) {
                if (pay.payState === 'success') {
                    this.closeOnsiteDialog();
                    return;
                }
                if (this.onsitePayDialogLocked) {
                    // 结果未定时一律不关：关窗会让收银员以为「没付」而重扫，钱可能已经扣了。
                    // 弹窗到点转「待人工核查」后本计算属性自动放开，收银员可正常关闭。
                    message("查询支付结果中，请勿关闭弹窗", "warning");
                    return;
                }
                if (!pay.isPayed || pay.payState === 'failed') {
                    // 还没扫过码 / 已明确失败：取消建单并释放号源。取消失败不得静默关弹窗——
                    // 若因「已收款成功」被拒，立即重查可收敛成成功态；其他失败让收银员看到再决定
                    if (pay.cancelRequesting) {
                        return;
                    }
                    pay.cancelRequesting = true;
                    return cancelOnsitePaid(session.reId).then(res => {
                        if (!this.isCurrentOnsitePaySession(session)) {
                            return;
                        }
                        if (res.code === 200) {
                            this.closeOnsiteDialog();
                        } else {
                            message(res.msg || '取消挂号单失败', 'warning');
                            this.requeryOnsitePayResult(false);
                        }
                    }).catch(error => {
                        if (this.isCurrentOnsitePaySession(session)) {
                            message((error && error.message) || '取消挂号单失败，请稍后重试', 'error');
                            this.requeryOnsitePayResult(false);
                        }
                    }).finally(() => {
                        if (this.isCurrentOnsitePaySession(session)) pay.cancelRequesting = false;
                    });
                    return;
                }
            }
            this.closeOnsiteDialog();
        },

        // 扫码枪 keydown 累积监听：只在付费挂号扫码步骤生效
        initOnsiteScanListener() {
            let barcode = '';
            let barcodeTimeout;
            const handleKeyDown = (e) => {
                if (!this.onsiteDialog.visible || this.onsitePay.step !== 'scan' || !this.onsitePay.isWaitPayLoading) {
                    return;
                }
                // 输入框内不拦截（避免干扰正常输入）
                const target = e.target;
                if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                    return;
                }
                if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt') {
                    return;
                }
                clearTimeout(barcodeTimeout);
                if (e.key === 'Enter') {
                    if (barcode.length > 0) {
                        if (barcode.indexOf("CapsLock") > -1) {
                            barcode = barcode.replace("CapsLock", "");
                        }
                        this.handleOnsiteScanCode(barcode);
                        barcode = '';
                    }
                } else {
                    barcode += e.key;
                }
                // 100ms 内没有新输入则认为扫码结束，丢弃半截条码
                barcodeTimeout = setTimeout(() => {
                    barcode = '';
                }, 100);
            };
            document.addEventListener('keydown', handleKeyDown);
            this.removeOnsiteScanListener = () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        },

        resetOnsiteForm() {
            if (this.onsitePatientSearchTimer) {
                clearTimeout(this.onsitePatientSearchTimer);
                this.onsitePatientSearchTimer = null;
            }
            this.onsitePatientSearchSeq++;
            this.onsiteForm = {
                doctorId: null,
                slotKey: '',
                cardId: null,
                cardPatientName: '',
                cardIdentity: '',
                cardPatientPhone: '',
                patientAction: null
            };
            this.onsitePatientKeyword = '';
            this.onsitePatientOptions = [];
            this.onsitePatientDropdownShow = false;
            this.onsiteMatch.visible = false;
            this.onsiteMatch.matchedList = [];
            this.onsiteMatch.selectedId = null;
            this.onsiteMatch.checking = false;
            this._onsiteMatchProceed = null;
            this._onsiteChosenCardId = null;
            this.invalidateOnsitePaySession();
            this.onsitePay = freshOnsitePay();
            if (this.$refs.onsiteFormRef) {
                this.$refs.onsiteFormRef.clearValidate();
            }
        },

        handleReturnVisit(row) {
            ElMessageBox.confirm('确定为该患者创建回诊记录？原预约状态不会改变。', '预约回诊', {
                confirmButtonText: '确定回诊', cancelButtonText: '取消', type: 'info'
            }).then(() => registerReturnVisit(row.reId)).then(res => {
                if (res.code === 200) {
                    message('回诊登记成功', 'success');
                    this.getUnifiedQueueInfo();
                } else message(res.msg || '回诊登记失败', 'warning');
            }).catch(error => {
                //用户点“取消”关弹窗时 reject 的是 'cancel'/'close' 字符串，不作错误提示；真实失败必须出声
                if (typeof error !== 'string') {
                    message(errorMsg(error, '回诊登记失败'), 'error');
                }
            });
        },
        //队列操作只留回诊的完成/取消。叫号统一在 clinic 门诊端操作，收费端队列只读
        handleQueueAction(item, action) {
            const api = action === 'complete' ? completeReturnVisit : cancelReturnVisit;
            const actionText = action === 'complete' ? '完成回诊' : '取消回诊';
            ElMessageBox.confirm(`确定${actionText}“${item.patientName || '该患者'}”？`, '提示', {
                confirmButtonText: '确定', cancelButtonText: '取消', type: 'info'
            }).then(() => api(item.id)).then(res => {
                if (res.code === 200) {
                    message(`${actionText}成功`, 'success');
                    this.getUnifiedQueueInfo();
                    this.getTodayReservationListInfo();
                    this.getTodayReservationStatInfo();
                } else message(res.msg || `${actionText}失败`, 'warning');
            }).catch(error => {
                //用户点“取消”关弹窗时 reject 的是 'cancel'/'close' 字符串，不作错误提示
                if (typeof error !== 'string') {
                    message(errorMsg(error, `${actionText}失败`), 'error');
                }
            });
        },

        // 查询今日预约统计数据
        getTodayReservationStatInfo() {
            const data = {
                schDate: getTodayDate(),
                doctorId: this.doctorInfo ? this.doctorInfo.doctorId : null,
            };
            getTodayReservationStat(data).then(res => {
                const rdata = res.data;
                if (rdata.code === 200 && rdata.data) {
                    const d = rdata.data;
                    this.statData = {
                        total:     d.total     || 0,
                        waitSign:  d.waitSign  || 0,
                        waitVisit: d.waitVisit || 0,
                        visiting:  d.visiting  || 0,
                        done:      d.done      || 0,
                        cancel:    d.cancel    || 0,
                        waitPay:   d.waitPay   || 0,
                        jumpCall:  d.jumpCall  || 0,
                    };
                    //注意不要用统计的医生全天总数覆盖 this.totalCount：
                    //列表查询带筛选条件，两者口径不同，覆盖会让分页器与表格行数打架（totalCount 只由列表接口回填）
                }
            }).catch(() => {});
        },

        /** 重置按钮操作 */
        resetQuery() {
            this.queryParams.cardPatientName = "";
            this.queryParams.cardPatientPhone = "";
            this.queryParams.reSerialNo = "";
            this.queryParams.reStatus = "";
            this.queryParams.isFree = "";
            this.currentPage = 1;
            this.getTodayReservationListInfo();
        },

        // 点击统计卡片筛选预约列表
        filterByStatus(status, isFree = '') {
            this.queryParams.reStatus = status;
            this.queryParams.isFree = isFree;
            this.currentPage = 1;
            this.getTodayReservationListInfo();
        },

        // 切换分页
        handleCurrentChange(val) {
            this.currentPage = val;
            this.getTodayReservationListInfo();
        },

        // 切换分页
        handleSizeChange(val) {
            this.pageNums = val;
            //每页条数变大后当前页可能超出新总页数，回第 1 页
            this.currentPage = 1;
            this.getTodayReservationListInfo();
        },

        // 查询今日预约列表
        getTodayReservationListInfo() {
            this.tableData = [];
            this.tableLoading = true;
            const data = {
                pageNum: this.currentPage,
                pageSize: this.pageNums,
                schDate: getTodayDate(),
                doctorId: this.doctorInfo ? this.doctorInfo.doctorId : null,
                cardPatientPhone: this.queryParams.cardPatientPhone,
                cardPatientName: this.queryParams.cardPatientName,
                reSerialNo: this.queryParams.reSerialNo,
                reStatus: this.queryParams.reStatus,
                isFree: this.queryParams.isFree,
                //后端按排队号升序排（与 clinic 端同口径）；前端 rows.sort 只能保证当页有序，跨页必须靠后端
                queueOrder: '1',
            };
            getTodayReservationList(data).then(res => {
                const rdata = res.data;
                this.tableLoading = false;
                if (rdata.code === 200) {
                    let rows = rdata.rows || [];
                    //排序与排队口径一致：排队号（签到/现场挂号顺序）升序，未签到（无排队号）排最后，
                    //同为无排队号按预约时间排；不再按预约时间点排序
                    rows.sort((a, b) => {
                        const seqA = a.queueSeq == null ? Number.MAX_SAFE_INTEGER : a.queueSeq;
                        const seqB = b.queueSeq == null ? Number.MAX_SAFE_INTEGER : b.queueSeq;
                        if (seqA !== seqB) return seqA - seqB;
                        return String(a.reTime || '').localeCompare(String(b.reTime || ''));
                    });
                    this.tableData = rows;
                    this.totalCount = rdata.total;

                    this.tableData.forEach((item) => {
                        let birYear = item.cardPatientBirth ? new Date(item.cardPatientBirth).getFullYear() : null;
                        let nowYear = new Date().getFullYear();
                        item.age = birYear ? parseInt(nowYear - birYear) : '--';
                        item.reTimeTypeText = item.reTimeType === 'am' ? '上午' : item.reTimeType === 'pm' ? '下午' : '晚上';
                        item.reStatusText = item.reStatus === '1' ? '待支付' : item.reStatus === '2' ?
                            '已完成' : item.reStatus === '3' ? '已失效' : item.reStatus === '4' ? '已取消' : item.reStatus === '5' ? '待就诊' : item.reStatus === '6' ? '就诊中' : item.reStatus === '7' ? '已过号' : item.reStatus === '8' ? '待签到' : '未知';
                        if (item.cardPatientSex === '0') {
                            item.sexText = '男';
                            item.avatar = item.cardPatientHeadPic ? this.toAvatarUrl(item.cardPatientHeadPic) : this.tencentCosUrl + "/tcmmsPic/manImg.png";
                        } else if (item.cardPatientSex === '1') {
                            item.sexText = '女';
                            item.avatar = item.cardPatientHeadPic ? this.toAvatarUrl(item.cardPatientHeadPic) : this.tencentCosUrl + "/tcmmsPic/womanImg.png";
                        } else {
                            item.sexText = '未知';
                            item.avatar = item.cardPatientHeadPic ? this.toAvatarUrl(item.cardPatientHeadPic) : this.tencentCosUrl + "/tcmmsPic/profile.jpg";
                        }
                    })
                }
            }).catch(error => {
                //axios reject 的是 Error 对象，直接丢给 ElMessage 会渲染成空白提示框
                message(errorMsg(error, '今日预约列表查询失败'), "error");
            });
        },

        toAvatarUrl(value) {
            return /^https?:\/\//i.test(value) ? value : this.tencentCosUrl + value;
        },

        // 签到
        handleSign(row) {
            const reId = row.reId;
            ElMessageBox.confirm('确定要签到？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'info',
            }).then(() => {
                signReOrder(reId).then(res => {
                    if (res.code === 200) {
                        //后端签到时分配当天排队号，随签到结果回传
                        message(res.data ? "签到成功，排队" + res.data + "号" : "签到成功", "success");
                        this.getTodayReservationStatInfo();
                        this.getTodayReservationListInfo();
                    } else {
                        message(res.msg || "签到失败", "warning");
                        this.getTodayReservationStatInfo();
                        this.getTodayReservationListInfo();
                    }
                }).catch(error => {
                    message(errorMsg(error, "签到失败"), "error");
                });
            }).catch(() => {});
        },

    }
}
